import React, { useState, useEffect, useCallback, useRef } from "react";
import { useParams, useNavigate, Link, useSearchParams } from "react-router-dom";
import Sidebar from "./components/Sidebar";
import Navbar from "./components/Navbar";
import {
  FiBell,
  FiSend,
  FiPlus,
  FiSearch,
  FiRefreshCw,
  FiTrash2,
  FiEdit2,
  FiEye,
  FiCalendar,
  FiChevronRight,
  FiArrowLeft,
  FiCheckCircle,
  FiAward,
  FiUser,
  FiUsers,
  FiX,
  FiExternalLink,
  FiRadio,
  FiCheck,
  FiLayers,
} from "react-icons/fi";
import toast from "react-hot-toast";
import {
  getEventNotifications,
  getEventNotificationById,
  createEventNotification,
  updateEventNotification,
  deleteEventNotification,
  dispatchPushNotification,
  sendTestPushNotification,
  NOTIFICATION_REDIRECT_ROUTES,
} from "../api/eventNotificationApi";
import { getEvents } from "../api/eventApi";
import { formatUtcDateTime } from "../utils/dateUtils";

const TYPE_CONFIG = {
  all: { label: "All Types", badgeClass: "bg-gray-100 text-gray-700 border-gray-200" },
  event_winner_push: {
    label: "Winner Push",
    badgeClass: "bg-emerald-50 text-emerald-700 border-emerald-200",
    dot: "bg-emerald-500",
  },
  event_reward: {
    label: "Reward Approval",
    badgeClass: "bg-blue-50 text-blue-700 border-blue-200",
    dot: "bg-blue-500",
  },
  event_notification: {
    label: "Custom Broadcast",
    badgeClass: "bg-amber-50 text-amber-700 border-amber-200",
    dot: "bg-amber-500",
  },
};

const EventNotifications = () => {
  const { id: routeEventId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();

  // Filters & query states
  const initialEventId = routeEventId || searchParams.get("eventId") || "all";
  const [selectedEventId, setSelectedEventId] = useState(initialEventId);
  const [selectedType, setSelectedType] = useState(searchParams.get("type") || "all");
  const [searchTerm, setSearchTerm] = useState(searchParams.get("search") || "");
  const [debouncedSearch, setDebouncedSearch] = useState(searchParams.get("search") || "");
  const [page, setPage] = useState(Number(searchParams.get("page")) || 1);
  const [limit, setLimit] = useState(10);

  // Data states
  const [notifications, setNotifications] = useState([]);
  const [eventsList, setEventsList] = useState([]);
  const [pagination, setPagination] = useState({
    total: 0,
    currentPage: 1,
    lastPage: 1,
    perPage: 10,
  });
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [stats, setStats] = useState({
    total: 0,
    winnerPush: 0,
    rewardApproval: 0,
    broadcast: 0,
  });

  // Modals state
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isDispatchModalOpen, setIsDispatchModalOpen] = useState(false);
  const [isTestPushModalOpen, setIsTestPushModalOpen] = useState(false);

  // Selected item states
  const [selectedItem, setSelectedItem] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);

  // Form states for Create / Edit
  const [formTitle, setFormTitle] = useState("");
  const [formMessage, setFormMessage] = useState("");
  const [formEventId, setFormEventId] = useState("");
  const [formUserId, setFormUserId] = useState("");
  const [formRedirectRoute, setFormRedirectRoute] = useState("/home");
  const [formCustomRoute, setFormCustomRoute] = useState("");
  const [formType, setFormType] = useState("event_notification");
  const [formImageSource, setFormImageSource] = useState("url"); // "url" | "file"
  const [formImageUrl, setFormImageUrl] = useState("");
  const [formImageFile, setFormImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState("");
  const fileInputRef = useRef(null);

  // Test Push Modal states
  const [testFcmToken, setTestFcmToken] = useState("");
  const [testTitle, setTestTitle] = useState("Test Event Notification");
  const [testMessage, setTestMessage] = useState("This is a direct test push from the Zeta Admin Panel.");
  const [testRoute, setTestRoute] = useState("/leaderboard");
  const [testEventId, setTestEventId] = useState("");

  // Debounce search
  const debounceTimer = useRef(null);
  const handleSearchChange = (e) => {
    const val = e.target.value;
    setSearchTerm(val);
    setPage(1);
    clearTimeout(debounceTimer.current);
    debounceTimer.current = setTimeout(() => {
      setDebouncedSearch(val);
    }, 350);
  };

  // Load events list for dropdown
  useEffect(() => {
    let isMounted = true;
    const fetchEvents = async () => {
      try {
        const res = await getEvents({ limit: 100, status: "all" });
        const list = res?.data?.events || res?.events || (Array.isArray(res?.data) ? res.data : []);
        if (isMounted) {
          setEventsList(list);
        }
      } catch (err) {
        console.warn("Failed to load events list for dropdown", err);
      }
    };
    fetchEvents();
    return () => {
      isMounted = false;
    };
  }, []);

  // Update URL search params cleanly
  useEffect(() => {
    const params = new URLSearchParams();
    if (selectedEventId && selectedEventId !== "all") params.set("eventId", selectedEventId);
    if (selectedType && selectedType !== "all") params.set("type", selectedType);
    if (debouncedSearch) params.set("search", debouncedSearch);
    if (page > 1) params.set("page", String(page));
    setSearchParams(params, { replace: true });
  }, [selectedEventId, selectedType, debouncedSearch, page, setSearchParams]);

  // Fetch notifications
  const fetchNotifications = useCallback(
    async (isRefresh = false) => {
      if (isRefresh) setRefreshing(true);
      else setLoading(true);

      try {
        const res = await getEventNotifications({
          page,
          limit,
          eventId: selectedEventId === "all" ? "" : selectedEventId,
          type: selectedType === "all" ? "" : selectedType,
          search: debouncedSearch,
        });

        const list = res?.data || (Array.isArray(res) ? res : []);
        setNotifications(Array.isArray(list) ? list : []);

        if (res?.pagination) {
          setPagination(res.pagination);
        } else {
          setPagination({
            total: Array.isArray(list) ? list.length : 0,
            currentPage: page,
            lastPage: Math.ceil((list?.length || 0) / limit) || 1,
            perPage: limit,
          });
        }

        // Count stats from current list or response if available
        if (Array.isArray(list)) {
          const winnerCount = list.filter((i) => i.type === "event_winner_push").length;
          const rewardCount = list.filter((i) => i.type === "event_reward").length;
          const broadcastCount = list.filter((i) => i.type === "event_notification").length;
          setStats({
            total: res?.pagination?.total ?? list.length,
            winnerPush: winnerCount,
            rewardApproval: rewardCount,
            broadcast: broadcastCount,
          });
        }
      } catch (err) {
        toast.error(err?.message || "Failed to load event notifications");
        setNotifications([]);
      } finally {
        setLoading(false);
        setRefreshing(false);
      }
    },
    [page, limit, selectedEventId, selectedType, debouncedSearch]
  );

  useEffect(() => {
    fetchNotifications();
  }, [fetchNotifications]);

  // Image handling in form
  const handleImageFileChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        toast.error("Image file size should not exceed 5MB");
        return;
      }
      setFormImageFile(file);
      const url = URL.createObjectURL(file);
      setImagePreview(url);
    }
  };

  const resetForm = () => {
    setFormTitle("");
    setFormMessage("");
    setFormEventId(selectedEventId !== "all" ? selectedEventId : "");
    setFormUserId("");
    setFormRedirectRoute("/home");
    setFormCustomRoute("");
    setFormType("event_notification");
    setFormImageSource("url");
    setFormImageUrl("");
    setFormImageFile(null);
    setImagePreview("");
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  // Open Create Modal
  const openCreateModal = () => {
    resetForm();
    setIsCreateModalOpen(true);
  };

  // Open Edit Modal
  const openEditModal = (item) => {
    setSelectedItem(item);
    setFormTitle(item.title || "");
    setFormMessage(item.message || "");
    setFormEventId(item.eventId || "");
    setFormUserId(item.userId ? String(item.userId) : "");
    const matchingRoute = NOTIFICATION_REDIRECT_ROUTES.find(
      (r) => r.value === item.redirectRoute
    );
    if (matchingRoute) {
      setFormRedirectRoute(item.redirectRoute);
      setFormCustomRoute("");
    } else if (item.redirectRoute) {
      setFormRedirectRoute("custom");
      setFormCustomRoute(item.redirectRoute);
    } else {
      setFormRedirectRoute("/home");
      setFormCustomRoute("");
    }
    setFormType(item.type || "event_notification");
    setFormImageSource("url");
    setFormImageUrl(item.imageUrl || "");
    setFormImageFile(null);
    setImagePreview(item.imageUrl || "");
    setIsEditModalOpen(true);
  };

  // Open View Modal
  const openViewModal = async (item) => {
    setSelectedItem(item);
    setIsViewModalOpen(true);
    // Optionally fetch full view from server
    try {
      const res = await getEventNotificationById(item.id);
      if (res?.data) {
        setSelectedItem(res.data);
      }
    } catch {
      // Keep existing item data
    }
  };

  // Open Dispatch Modal
  const openDispatchModal = (item) => {
    setSelectedItem(item);
    setIsDispatchModalOpen(true);
  };

  // Open Delete Modal
  const openDeleteModal = (item) => {
    setSelectedItem(item);
    setIsDeleteModalOpen(true);
  };

  // Handle Create Notification Submit
  const handleCreateSubmit = async (e) => {
    e.preventDefault();
    if (!formTitle.trim()) {
      toast.error("Please enter a notification title");
      return;
    }
    if (!formMessage.trim()) {
      toast.error("Please enter a notification message");
      return;
    }

    const finalRoute =
      formRedirectRoute === "custom"
        ? formCustomRoute.trim() || "/home"
        : formRedirectRoute;

    setActionLoading(true);
    try {
      let payload;
      if (formImageSource === "file" && formImageFile) {
        const formData = new FormData();
        formData.append("title", formTitle.trim());
        formData.append("message", formMessage.trim());
        if (formEventId) formData.append("eventId", formEventId);
        if (formUserId) formData.append("userId", Number(formUserId));
        formData.append("redirectRoute", finalRoute);
        formData.append("type", formType);
        formData.append("image", formImageFile);
        payload = formData;
      } else {
        payload = {
          title: formTitle.trim(),
          message: formMessage.trim(),
          eventId: formEventId || undefined,
          userId: formUserId ? Number(formUserId) : undefined,
          redirectRoute: finalRoute,
          type: formType,
          imageUrl: formImageUrl.trim() || undefined,
        };
      }

      await createEventNotification(payload);
      toast.success("Event notification created successfully!");
      setIsCreateModalOpen(false);
      resetForm();
      fetchNotifications();
    } catch (err) {
      toast.error(err?.message || "Failed to create event notification");
    } finally {
      setActionLoading(false);
    }
  };

  // Handle Edit Notification Submit
  const handleEditSubmit = async (e) => {
    e.preventDefault();
    if (!selectedItem) return;
    if (!formTitle.trim()) {
      toast.error("Please enter a notification title");
      return;
    }
    if (!formMessage.trim()) {
      toast.error("Please enter a notification message");
      return;
    }

    const finalRoute =
      formRedirectRoute === "custom"
        ? formCustomRoute.trim() || "/home"
        : formRedirectRoute;

    setActionLoading(true);
    try {
      let payload;
      if (formImageSource === "file" && formImageFile) {
        const formData = new FormData();
        formData.append("title", formTitle.trim());
        formData.append("message", formMessage.trim());
        if (formEventId) formData.append("eventId", formEventId);
        if (formUserId) formData.append("userId", Number(formUserId));
        formData.append("redirectRoute", finalRoute);
        formData.append("image", formImageFile);
        payload = formData;
      } else {
        payload = {
          title: formTitle.trim(),
          message: formMessage.trim(),
          eventId: formEventId || undefined,
          userId: formUserId ? Number(formUserId) : undefined,
          redirectRoute: finalRoute,
          imageUrl: formImageUrl.trim() || undefined,
        };
      }

      await updateEventNotification(selectedItem.id, payload);
      toast.success("Event notification updated successfully!");
      setIsEditModalOpen(false);
      fetchNotifications();
    } catch (err) {
      toast.error(err?.message || "Failed to update notification");
    } finally {
      setActionLoading(false);
    }
  };

  // Handle Delete Submit
  const handleDeleteSubmit = async () => {
    if (!selectedItem) return;
    setActionLoading(true);
    try {
      await deleteEventNotification(selectedItem.id);
      toast.success("Notification deleted successfully!");
      setIsDeleteModalOpen(false);
      setSelectedItem(null);
      fetchNotifications();
    } catch (err) {
      toast.error(err?.message || "Failed to delete notification");
    } finally {
      setActionLoading(false);
    }
  };

  // Handle Dispatch Push Notification Submit
  const handleDispatchSubmit = async () => {
    if (!selectedItem) return;
    setActionLoading(true);
    try {
      const res = await dispatchPushNotification(selectedItem.id);
      const data = res?.data;
      if (data && typeof data === "object") {
        toast.success(
          `Push dispatched! Sent: ${data.sent ?? 0}, Failed: ${data.failed ?? 0} (Total: ${
            data.recipientCount ?? 0
          })`
        );
      } else {
        toast.success(res?.message || "Notification dispatched successfully!");
      }
      setIsDispatchModalOpen(false);
      setSelectedItem(null);
    } catch (err) {
      toast.error(err?.message || "Failed to dispatch push notification");
    } finally {
      setActionLoading(false);
    }
  };

  // Handle Test Push Submit
  const handleTestPushSubmit = async (e) => {
    e.preventDefault();
    if (!testFcmToken.trim()) {
      toast.error("Please enter a target FCM device token");
      return;
    }
    if (!testTitle.trim() || !testMessage.trim()) {
      toast.error("Please enter Title and Message for test push");
      return;
    }

    setActionLoading(true);
    try {
      await sendTestPushNotification({
        fcmToken: testFcmToken.trim(),
        title: testTitle.trim(),
        message: testMessage.trim(),
        redirectRoute: testRoute,
        eventId: testEventId || undefined,
      });
      toast.success("Test push notification sent successfully!");
      setIsTestPushModalOpen(false);
    } catch (err) {
      toast.error(err?.message || "Failed to send test push notification");
    } finally {
      setActionLoading(false);
    }
  };

  const getTargetAudienceText = (item) => {
    if (item.userId) {
      return {
        type: "user",
        label: `User #${item.userId}`,
        sub: item.recipientUsername
          ? `@${item.recipientUsername}`
          : item.recipientEmail || "Specific User",
        badge: "bg-purple-50 text-purple-700 border-purple-200",
      };
    }
    if (item.eventId) {
      const matched = eventsList.find((e) => (e.eventId || e.id) === item.eventId);
      return {
        type: "event",
        label: item.eventName || matched?.eventName || item.eventId,
        sub: "Event Participants",
        badge: "bg-blue-50 text-blue-700 border-blue-200",
      };
    }
    return {
      type: "all",
      label: "All Users",
      sub: "Global App Broadcast",
      badge: "bg-gray-100 text-gray-700 border-gray-200",
    };
  };

  return (
    <div className="flex bg-gray-100 min-h-screen">
      <Sidebar />
      <div className="flex-1 flex flex-col min-w-0">
        <Navbar />

        <div className="p-4 sm:p-8 space-y-6 max-w-7xl mx-auto w-full">
          {/* Breadcrumb Navigation */}
          <div className="flex items-center flex-wrap gap-1.5 text-xs font-medium text-gray-500">
            <Link
              to="/events"
              className="hover:text-black transition flex items-center gap-1 hover:underline"
            >
              <FiCalendar size={13} /> Events
            </Link>
            <FiChevronRight size={12} className="text-gray-400 flex-shrink-0" />
            {selectedEventId && selectedEventId !== "all" ? (
              <>
                <Link
                  to={`/events/${selectedEventId}`}
                  className="hover:text-black transition max-w-xs truncate font-medium hover:underline"
                >
                  {eventsList.find((e) => (e.eventId || e.id) === selectedEventId)
                    ?.eventName || selectedEventId}
                </Link>
                <FiChevronRight size={12} className="text-gray-400 flex-shrink-0" />
              </>
            ) : null}
            <span className="text-black font-bold bg-[#ffffcc] px-2.5 py-0.5 rounded border border-[#e6e600]/40">
              Event Notifications
            </span>
          </div>

          {/* Sub Navigation Tabs */}
          <div className="flex items-center gap-2 border-b border-gray-200 pb-3 overflow-x-auto text-sm">
            <Link
              to="/events"
              className="px-3.5 py-1.5 rounded-xl font-medium text-gray-600 hover:text-gray-900 hover:bg-white transition flex items-center gap-1.5 whitespace-nowrap"
            >
              <FiCalendar size={15} /> All Events
            </Link>
            <Link
              to="/events/winners"
              className="px-3.5 py-1.5 rounded-xl font-medium text-gray-600 hover:text-gray-900 hover:bg-white transition flex items-center gap-1.5 whitespace-nowrap"
            >
              <FiAward size={15} /> Reward Coin Approval
            </Link>
            <span className="px-3.5 py-1.5 rounded-xl font-bold bg-black text-white flex items-center gap-1.5 shadow-xs whitespace-nowrap">
              <FiBell size={15} /> Event Notifications
            </span>
          </div>

          {/* Page Header */}
          <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={() => {
                  if (window.history.length > 1) navigate(-1);
                  else navigate("/events");
                }}
                className="p-2.5 bg-white hover:bg-gray-50 border border-gray-200 rounded-xl text-gray-700 hover:text-black transition shadow-xs cursor-pointer flex items-center justify-center"
                title="Back"
                aria-label="Back"
              >
                <FiArrowLeft size={18} />
              </button>
              <div>
                <h1 className="text-2xl font-black text-gray-900 tracking-tight flex items-center gap-2">
                  Event Notifications
                  <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 border border-amber-200">
                    Push Ledger & Dispatch
                  </span>
                </h1>
                <p className="text-xs text-gray-500 mt-0.5">
                  Manage automatic reward push records, winner notifications, and custom event broadcasts
                </p>
              </div>
            </div>

            {/* Top Action Buttons */}
            <div className="flex flex-wrap items-center gap-2.5">
              <button
                type="button"
                onClick={() => fetchNotifications(true)}
                disabled={refreshing}
                className="p-2.5 bg-white hover:bg-gray-50 border border-gray-200 rounded-xl text-gray-700 hover:text-black transition shadow-xs cursor-pointer flex items-center justify-center"
                title="Refresh Notifications"
              >
                <FiRefreshCw size={16} className={refreshing ? "animate-spin" : ""} />
              </button>

              <button
                type="button"
                onClick={() => setIsTestPushModalOpen(true)}
                className="px-3.5 py-2.5 bg-white hover:bg-gray-50 border border-gray-200 rounded-xl text-xs font-bold text-gray-700 hover:text-black transition shadow-xs flex items-center gap-1.5 cursor-pointer"
              >
                <FiRadio size={14} className="text-amber-500" />
                <span>Test FCM Push</span>
              </button>

              <button
                type="button"
                onClick={openCreateModal}
                className="px-4 py-2.5 bg-black hover:bg-gray-800 text-white rounded-xl text-xs font-bold transition shadow-sm flex items-center gap-1.5 cursor-pointer"
              >
                <FiPlus size={16} />
                <span>Create Notification</span>
              </button>
            </div>
          </div>

          {/* Stats Bar */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
            <div className="bg-white p-4 rounded-2xl border border-gray-200 shadow-xs flex items-center justify-between">
              <div>
                <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
                  Total Entries
                </span>
                <p className="text-2xl font-black text-gray-900 mt-1">
                  {pagination.total || stats.total}
                </p>
                <p className="text-[11px] text-gray-400 mt-0.5">Recorded pushes</p>
              </div>
              <div className="w-11 h-11 rounded-xl bg-gray-50 border border-gray-200 flex items-center justify-center text-gray-700">
                <FiBell size={20} />
              </div>
            </div>

            <div className="bg-white p-4 rounded-2xl border border-gray-200 shadow-xs flex items-center justify-between">
              <div>
                <span className="text-xs font-semibold text-emerald-600 uppercase tracking-wider">
                  Winner Pushes
                </span>
                <p className="text-2xl font-black text-gray-900 mt-1">
                  {stats.winnerPush}
                </p>
                <p className="text-[11px] text-emerald-600 mt-0.5">Individual alerts</p>
              </div>
              <div className="w-11 h-11 rounded-xl bg-emerald-50 border border-emerald-200 flex items-center justify-center text-emerald-600">
                <FiAward size={20} />
              </div>
            </div>

            <div className="bg-white p-4 rounded-2xl border border-gray-200 shadow-xs flex items-center justify-between">
              <div>
                <span className="text-xs font-semibold text-blue-600 uppercase tracking-wider">
                  Reward Approvals
                </span>
                <p className="text-2xl font-black text-gray-900 mt-1">
                  {stats.rewardApproval}
                </p>
                <p className="text-[11px] text-blue-600 mt-0.5">Auto-credit logs</p>
              </div>
              <div className="w-11 h-11 rounded-xl bg-blue-50 border border-blue-200 flex items-center justify-center text-blue-600">
                <FiCheckCircle size={20} />
              </div>
            </div>

            <div className="bg-white p-4 rounded-2xl border border-gray-200 shadow-xs flex items-center justify-between">
              <div>
                <span className="text-xs font-semibold text-amber-600 uppercase tracking-wider">
                  Broadcasts
                </span>
                <p className="text-2xl font-black text-gray-900 mt-1">
                  {stats.broadcast}
                </p>
                <p className="text-[11px] text-amber-600 mt-0.5">Custom broadcasts</p>
              </div>
              <div className="w-11 h-11 rounded-xl bg-amber-50 border border-amber-200 flex items-center justify-center text-amber-600">
                <FiSend size={20} />
              </div>
            </div>
          </div>

          {/* Filter, Search and Type Bar */}
          <div className="bg-white p-4 rounded-2xl border border-gray-200 shadow-xs space-y-3">
            <div className="flex flex-col md:flex-row items-stretch md:items-center justify-between gap-3">
              {/* Event Selector Filter */}
              <div className="flex items-center gap-2 flex-1 max-w-md">
                <div className="flex items-center gap-1.5 bg-gray-50 border border-gray-200 rounded-xl px-3 py-2 text-xs w-full">
                  <FiCalendar className="text-gray-400 flex-shrink-0" size={15} />
                  <span className="text-gray-500 font-medium whitespace-nowrap">Filter Event:</span>
                  <select
                    value={selectedEventId}
                    onChange={(e) => {
                      setSelectedEventId(e.target.value);
                      setPage(1);
                    }}
                    className="bg-transparent font-bold text-gray-800 focus:outline-none cursor-pointer w-full truncate"
                  >
                    <option value="all">🌟 All Events (Combined)</option>
                    {eventsList.map((evt) => (
                      <option key={evt.eventId || evt.id} value={evt.eventId || evt.id}>
                        {evt.eventName} ({evt.status || "event"})
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Search Bar */}
              <div className="relative flex-1 max-w-md">
                <FiSearch
                  size={15}
                  className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400"
                />
                <input
                  type="text"
                  placeholder="Search title, message, username, email..."
                  value={searchTerm}
                  onChange={handleSearchChange}
                  className="w-full pl-9 pr-8 py-2 bg-gray-50 border border-gray-200 rounded-xl text-xs text-gray-900 focus:outline-none focus:ring-1 focus:ring-black placeholder-gray-400"
                />
                {searchTerm && (
                  <button
                    type="button"
                    onClick={() => {
                      setSearchTerm("");
                      setDebouncedSearch("");
                      setPage(1);
                    }}
                    className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 p-0.5"
                  >
                    <FiX size={14} />
                  </button>
                )}
              </div>
            </div>

            {/* Type Filter Pills & Limit */}
            <div className="flex flex-wrap items-center justify-between gap-2 pt-2 border-t border-gray-100">
              <div className="flex flex-wrap items-center gap-1.5">
                <span className="text-[11px] font-bold text-gray-400 uppercase mr-1">
                  Type:
                </span>
                {Object.keys(TYPE_CONFIG).map((typeKey) => {
                  const cfg = TYPE_CONFIG[typeKey];
                  const isActive = selectedType === typeKey;
                  return (
                    <button
                      key={typeKey}
                      type="button"
                      onClick={() => {
                        setSelectedType(typeKey);
                        setPage(1);
                      }}
                      className={`px-3 py-1 rounded-xl text-xs font-semibold transition border ${
                        isActive
                          ? "bg-black text-white border-black shadow-xs"
                          : "bg-gray-50 text-gray-600 border-gray-200 hover:bg-gray-100"
                      }`}
                    >
                      {cfg.label}
                    </button>
                  );
                })}
              </div>

              {/* Page Limit Selector */}
              <div className="flex items-center gap-1 text-xs text-gray-500">
                <span>Show:</span>
                <select
                  value={limit}
                  onChange={(e) => {
                    setLimit(Number(e.target.value));
                    setPage(1);
                  }}
                  className="bg-gray-50 border border-gray-200 rounded-lg px-2 py-1 font-bold text-gray-800 focus:outline-none"
                >
                  <option value={10}>10</option>
                  <option value={25}>25</option>
                  <option value={50}>50</option>
                </select>
                <span>rows</span>
              </div>
            </div>
          </div>

          {/* Table of Notifications */}
          <div className="bg-white rounded-2xl border border-gray-200 shadow-xs overflow-hidden">
            {loading ? (
              <div className="py-20 flex flex-col items-center justify-center gap-3 text-gray-400">
                <FiRefreshCw size={26} className="animate-spin text-gray-800" />
                <p className="text-xs font-medium">Loading event notifications...</p>
              </div>
            ) : notifications.length === 0 ? (
              <div className="py-16 text-center space-y-3">
                <div className="w-14 h-14 bg-gray-50 border border-gray-200 rounded-2xl mx-auto flex items-center justify-center text-gray-400">
                  <FiBell size={24} />
                </div>
                <h3 className="text-base font-bold text-gray-800">
                  No Event Notifications Found
                </h3>
                <p className="text-xs text-gray-400 max-w-sm mx-auto">
                  {searchTerm || selectedEventId !== "all" || selectedType !== "all"
                    ? "Try adjusting your search query, event filter, or type filter."
                    : "No push notifications have been created or dispatched for events yet."}
                </p>
                <div className="pt-2">
                  <button
                    type="button"
                    onClick={openCreateModal}
                    className="px-4 py-2 bg-black hover:bg-gray-800 text-white rounded-xl text-xs font-bold transition inline-flex items-center gap-1.5"
                  >
                    <FiPlus size={14} /> Create New Notification
                  </button>
                </div>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-left text-xs">
                  <thead>
                    <tr className="bg-gray-50/80 border-b border-gray-200 text-gray-500 uppercase tracking-wider font-semibold">
                      <th className="px-4 py-3.5">Content & Info</th>
                      <th className="px-4 py-3.5">Event</th>
                      <th className="px-4 py-3.5">Target Audience</th>
                      <th className="px-4 py-3.5">Type</th>
                      <th className="px-4 py-3.5">App Route</th>
                      <th className="px-4 py-3.5">Created At (UTC)</th>
                      <th className="px-4 py-3.5 text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {notifications.map((item) => {
                      const typeCfg =
                        TYPE_CONFIG[item.type] || TYPE_CONFIG.event_notification;
                      const targetInfo = getTargetAudienceText(item);

                      return (
                        <tr
                          key={item.id}
                          className="hover:bg-gray-50/60 transition group"
                        >
                          {/* Title & Message preview with optional thumbnail */}
                          <td className="px-4 py-3.5 max-w-xs">
                            <div className="flex items-start gap-2.5">
                              {item.imageUrl ? (
                                <img
                                  src={item.imageUrl}
                                  alt="Thumb"
                                  className="w-10 h-10 rounded-lg object-cover border border-gray-200 flex-shrink-0 bg-gray-50 mt-0.5"
                                  onError={(e) => {
                                    e.target.style.display = "none";
                                  }}
                                />
                              ) : (
                                <div className="w-9 h-9 rounded-lg bg-gray-100 border border-gray-200 flex items-center justify-center text-gray-400 flex-shrink-0 mt-0.5">
                                  <FiBell size={15} />
                                </div>
                              )}
                              <div className="min-w-0 flex-1">
                                <p className="font-bold text-gray-900 truncate flex items-center gap-1.5">
                                  <span>{item.title}</span>
                                </p>
                                <p className="text-[11px] text-gray-500 line-clamp-2 mt-0.5 leading-relaxed">
                                  {item.message}
                                </p>
                              </div>
                            </div>
                          </td>

                          {/* Event Name */}
                          <td className="px-4 py-3.5 whitespace-nowrap">
                            {item.eventName || item.eventId ? (
                              <div>
                                <span className="font-bold text-gray-800 block truncate max-w-[160px]">
                                  {item.eventName || item.eventId}
                                </span>
                                {item.eventId && (
                                  <span className="text-[10px] text-gray-400 font-mono">
                                    {item.eventId}
                                  </span>
                                )}
                              </div>
                            ) : (
                              <span className="text-gray-400 italic">Global App</span>
                            )}
                          </td>

                          {/* Target Audience */}
                          <td className="px-4 py-3.5 whitespace-nowrap">
                            <div className="space-y-0.5">
                              <span
                                className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[11px] font-bold border ${targetInfo.badge}`}
                              >
                                {targetInfo.type === "user" && <FiUser size={11} />}
                                {targetInfo.type === "event" && <FiUsers size={11} />}
                                {targetInfo.type === "all" && <FiLayers size={11} />}
                                {targetInfo.label}
                              </span>
                              <p className="text-[10px] text-gray-400 truncate max-w-[150px]">
                                {targetInfo.sub}
                              </p>
                            </div>
                          </td>

                          {/* Type */}
                          <td className="px-4 py-3.5 whitespace-nowrap">
                            <span
                              className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-bold border ${typeCfg.badgeClass}`}
                            >
                              <span
                                className={`w-1.5 h-1.5 rounded-full ${
                                  typeCfg.dot || "bg-gray-400"
                                }`}
                              />
                              {typeCfg.label}
                            </span>
                          </td>

                          {/* App Route */}
                          <td className="px-4 py-3.5 whitespace-nowrap">
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-gray-100 text-gray-700 font-mono text-[11px] border border-gray-200">
                              <FiExternalLink size={10} className="text-gray-400" />
                              {item.redirectRoute || "/home"}
                            </span>
                          </td>

                          {/* Created At */}
                          <td className="px-4 py-3.5 whitespace-nowrap text-gray-500 font-mono text-[11px]">
                            {formatUtcDateTime(item.createdAt)}
                          </td>

                          {/* Action Buttons */}
                          <td className="px-4 py-3.5 whitespace-nowrap text-right">
                            <div className="flex items-center justify-end gap-1">
                              {/* Send Push Action */}
                              <button
                                type="button"
                                onClick={() => openDispatchModal(item)}
                                className="p-2 text-emerald-600 hover:bg-emerald-50 rounded-lg transition border border-transparent hover:border-emerald-200"
                                title="Dispatch FCM Push"
                              >
                                <FiSend size={14} />
                              </button>

                              {/* View Action */}
                              <button
                                type="button"
                                onClick={() => openViewModal(item)}
                                className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition border border-transparent hover:border-blue-200"
                                title="View Details"
                              >
                                <FiEye size={14} />
                              </button>

                              {/* Edit Action */}
                              <button
                                type="button"
                                onClick={() => openEditModal(item)}
                                className="p-2 text-gray-600 hover:bg-gray-100 rounded-lg transition border border-transparent hover:border-gray-300"
                                title="Edit"
                              >
                                <FiEdit2 size={14} />
                              </button>

                              {/* Delete Action */}
                              <button
                                type="button"
                                onClick={() => openDeleteModal(item)}
                                className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition border border-transparent hover:border-red-200"
                                title="Delete"
                              >
                                <FiTrash2 size={14} />
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}

            {/* Pagination footer */}
            {pagination.total > 0 && (
              <div className="p-4 border-t border-gray-100 flex flex-col sm:flex-row items-center justify-between gap-3 text-xs text-gray-500">
                <p>
                  Showing{" "}
                  <span className="font-bold text-gray-900">
                    {Math.min(
                      (pagination.currentPage - 1) * pagination.perPage + 1,
                      pagination.total
                    )}
                  </span>{" "}
                  to{" "}
                  <span className="font-bold text-gray-900">
                    {Math.min(
                      pagination.currentPage * pagination.perPage,
                      pagination.total
                    )}
                  </span>{" "}
                  of <span className="font-bold text-gray-900">{pagination.total}</span>{" "}
                  records
                </p>

                <div className="flex items-center gap-1.5">
                  <button
                    type="button"
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    disabled={page <= 1}
                    className="px-3 py-1.5 rounded-lg border border-gray-200 bg-white hover:bg-gray-50 text-gray-700 disabled:opacity-40 disabled:cursor-not-allowed transition font-semibold"
                  >
                    Previous
                  </button>
                  <span className="px-2 py-1 font-bold text-gray-800">
                    {pagination.currentPage} / {pagination.lastPage || 1}
                  </span>
                  <button
                    type="button"
                    onClick={() => setPage((p) => Math.min(pagination.lastPage, p + 1))}
                    disabled={page >= pagination.lastPage}
                    className="px-3 py-1.5 rounded-lg border border-gray-200 bg-white hover:bg-gray-50 text-gray-700 disabled:opacity-40 disabled:cursor-not-allowed transition font-semibold"
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* ======================================================== */}
      {/* 1. CREATE EVENT NOTIFICATION MODAL */}
      {/* ======================================================== */}
      {isCreateModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-xs overflow-y-auto">
          <div className="bg-white rounded-3xl max-w-lg w-full p-6 shadow-2xl border border-gray-100 space-y-4 my-8">
            <div className="flex items-center justify-between border-b border-gray-100 pb-3">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-lg bg-black text-white flex items-center justify-center">
                  <FiPlus size={16} />
                </div>
                <div>
                  <h3 className="text-base font-bold text-gray-900">
                    Create Event Notification
                  </h3>
                  <p className="text-[11px] text-gray-400">
                    Draft a new event notification or broadcast
                  </p>
                </div>
              </div>
              <button
                type="button"
                onClick={() => setIsCreateModalOpen(false)}
                className="text-gray-400 hover:text-black p-1 transition"
              >
                <FiX size={18} />
              </button>
            </div>

            <form onSubmit={handleCreateSubmit} className="space-y-4 text-xs">
              {/* Title */}
              <div>
                <label className="block font-bold text-gray-700 mb-1">
                  Notification Title <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  required
                  placeholder="e.g. Tournament Results Announced!"
                  value={formTitle}
                  onChange={(e) => setFormTitle(e.target.value)}
                  className="w-full px-3 py-2 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:ring-1 focus:ring-black"
                />
              </div>

              {/* Message */}
              <div>
                <div className="flex items-center justify-between mb-1">
                  <label className="block font-bold text-gray-700">
                    Notification Message <span className="text-red-500">*</span>
                  </label>
                  <span className="text-[10px] text-gray-400 font-mono">
                    {formMessage.length} characters
                  </span>
                </div>
                <textarea
                  required
                  rows={3}
                  placeholder="Check the event leaderboard now to claim your token rewards..."
                  value={formMessage}
                  onChange={(e) => setFormMessage(e.target.value)}
                  className="w-full px-3 py-2 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:ring-1 focus:ring-black resize-none"
                />
              </div>

              {/* Associated Event Selector */}
              <div>
                <label className="block font-bold text-gray-700 mb-1">
                  Associated Event (Optional)
                </label>
                <select
                  value={formEventId}
                  onChange={(e) => setFormEventId(e.target.value)}
                  className="w-full px-3 py-2 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:ring-1 focus:ring-black font-semibold text-gray-800"
                >
                  <option value="">None (All App Users / Global Broadcast)</option>
                  {eventsList.map((evt) => (
                    <option key={evt.eventId || evt.id} value={evt.eventId || evt.id}>
                      {evt.eventName} ({evt.eventId || evt.id})
                    </option>
                  ))}
                </select>
                <p className="text-[10px] text-gray-400 mt-0.5">
                  If selected and User ID is empty, pushes to all registered participants of this event.
                </p>
              </div>

              {/* Target User ID */}
              <div>
                <label className="block font-bold text-gray-700 mb-1">
                  Specific User ID (Optional)
                </label>
                <input
                  type="number"
                  placeholder="Leave empty for event-wide or broadcast"
                  value={formUserId}
                  onChange={(e) => setFormUserId(e.target.value)}
                  className="w-full px-3 py-2 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:ring-1 focus:ring-black"
                />
              </div>

              {/* Deep Link Route */}
              <div>
                <label className="block font-bold text-gray-700 mb-1">
                  Mobile App Redirect Route
                </label>
                <select
                  value={formRedirectRoute}
                  onChange={(e) => setFormRedirectRoute(e.target.value)}
                  className="w-full px-3 py-2 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:ring-1 focus:ring-black font-medium text-gray-800"
                >
                  {NOTIFICATION_REDIRECT_ROUTES.map((route) => (
                    <option key={route.value} value={route.value}>
                      {route.icon} {route.label} ({route.value})
                    </option>
                  ))}
                  <option value="custom">Custom Path...</option>
                </select>

                {formRedirectRoute === "custom" && (
                  <input
                    type="text"
                    placeholder="e.g. /custom-route"
                    value={formCustomRoute}
                    onChange={(e) => setFormCustomRoute(e.target.value)}
                    className="mt-1.5 w-full px-3 py-2 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:ring-1 focus:ring-black"
                  />
                )}
              </div>

              {/* Type */}
              <div>
                <label className="block font-bold text-gray-700 mb-1">
                  Notification Type
                </label>
                <select
                  value={formType}
                  onChange={(e) => setFormType(e.target.value)}
                  className="w-full px-3 py-2 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:ring-1 focus:ring-black font-semibold text-gray-800"
                >
                  <option value="event_notification">Custom Broadcast (event_notification)</option>
                  <option value="event_winner_push">Winner Push (event_winner_push)</option>
                  <option value="event_reward">Reward Approval (event_reward)</option>
                </select>
              </div>

              {/* Image Source Toggle */}
              <div>
                <div className="flex items-center justify-between mb-1.5">
                  <label className="font-bold text-gray-700">Notification Image (Optional)</label>
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => {
                        setFormImageSource("url");
                        setImagePreview(formImageUrl);
                      }}
                      className={`text-[10px] font-bold px-2 py-0.5 rounded ${
                        formImageSource === "url"
                          ? "bg-black text-white"
                          : "bg-gray-100 text-gray-600"
                      }`}
                    >
                      Image URL
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setFormImageSource("file");
                        setImagePreview(
                          formImageFile ? URL.createObjectURL(formImageFile) : ""
                        );
                      }}
                      className={`text-[10px] font-bold px-2 py-0.5 rounded ${
                        formImageSource === "file"
                          ? "bg-black text-white"
                          : "bg-gray-100 text-gray-600"
                      }`}
                    >
                      Upload File
                    </button>
                  </div>
                </div>

                {formImageSource === "url" ? (
                  <input
                    type="url"
                    placeholder="https://cdn.example.com/banner.png"
                    value={formImageUrl}
                    onChange={(e) => {
                      setFormImageUrl(e.target.value);
                      setImagePreview(e.target.value);
                    }}
                    className="w-full px-3 py-2 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:ring-1 focus:ring-black"
                  />
                ) : (
                  <div>
                    <input
                      type="file"
                      ref={fileInputRef}
                      accept="image/*"
                      onChange={handleImageFileChange}
                      className="w-full text-xs text-gray-500 file:mr-3 file:py-1.5 file:px-3 file:rounded-xl file:border-0 file:text-xs file:font-semibold file:bg-gray-100 file:text-gray-700 hover:file:bg-gray-200"
                    />
                  </div>
                )}

                {imagePreview && (
                  <div className="mt-2 relative inline-block">
                    <img
                      src={imagePreview}
                      alt="Preview"
                      className="h-16 w-28 object-cover rounded-xl border border-gray-200"
                      onError={() => setImagePreview("")}
                    />
                    <button
                      type="button"
                      onClick={() => {
                        setImagePreview("");
                        setFormImageUrl("");
                        setFormImageFile(null);
                        if (fileInputRef.current) fileInputRef.current.value = "";
                      }}
                      className="absolute -top-1.5 -right-1.5 bg-red-600 text-white rounded-full p-0.5"
                    >
                      <FiX size={12} />
                    </button>
                  </div>
                )}
              </div>

              {/* Form Action Buttons */}
              <div className="flex items-center justify-end gap-2 pt-3 border-t border-gray-100">
                <button
                  type="button"
                  onClick={() => setIsCreateModalOpen(false)}
                  className="px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-xl font-bold transition"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={actionLoading}
                  className="px-5 py-2 bg-black hover:bg-gray-800 text-white rounded-xl font-bold transition flex items-center gap-1.5 disabled:opacity-50"
                >
                  {actionLoading ? (
                    <>
                      <FiRefreshCw size={13} className="animate-spin" />
                      <span>Creating...</span>
                    </>
                  ) : (
                    <>
                      <FiCheck size={14} />
                      <span>Create Notification</span>
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ======================================================== */}
      {/* 2. EDIT EVENT NOTIFICATION MODAL */}
      {/* ======================================================== */}
      {isEditModalOpen && selectedItem && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-xs overflow-y-auto">
          <div className="bg-white rounded-3xl max-w-lg w-full p-6 shadow-2xl border border-gray-100 space-y-4 my-8">
            <div className="flex items-center justify-between border-b border-gray-100 pb-3">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-lg bg-black text-white flex items-center justify-center">
                  <FiEdit2 size={15} />
                </div>
                <div>
                  <h3 className="text-base font-bold text-gray-900">
                    Edit Notification #{selectedItem.id}
                  </h3>
                  <p className="text-[11px] text-gray-400">
                    Update notification title, message, or redirect route
                  </p>
                </div>
              </div>
              <button
                type="button"
                onClick={() => setIsEditModalOpen(false)}
                className="text-gray-400 hover:text-black p-1 transition"
              >
                <FiX size={18} />
              </button>
            </div>

            <form onSubmit={handleEditSubmit} className="space-y-4 text-xs">
              <div>
                <label className="block font-bold text-gray-700 mb-1">
                  Notification Title <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  required
                  value={formTitle}
                  onChange={(e) => setFormTitle(e.target.value)}
                  className="w-full px-3 py-2 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:ring-1 focus:ring-black"
                />
              </div>

              <div>
                <label className="block font-bold text-gray-700 mb-1">
                  Notification Message <span className="text-red-500">*</span>
                </label>
                <textarea
                  required
                  rows={3}
                  value={formMessage}
                  onChange={(e) => setFormMessage(e.target.value)}
                  className="w-full px-3 py-2 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:ring-1 focus:ring-black resize-none"
                />
              </div>

              <div>
                <label className="block font-bold text-gray-700 mb-1">
                  Mobile App Redirect Route
                </label>
                <select
                  value={formRedirectRoute}
                  onChange={(e) => setFormRedirectRoute(e.target.value)}
                  className="w-full px-3 py-2 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:ring-1 focus:ring-black font-medium text-gray-800"
                >
                  {NOTIFICATION_REDIRECT_ROUTES.map((route) => (
                    <option key={route.value} value={route.value}>
                      {route.icon} {route.label} ({route.value})
                    </option>
                  ))}
                  <option value="custom">Custom Path...</option>
                </select>

                {formRedirectRoute === "custom" && (
                  <input
                    type="text"
                    placeholder="e.g. /custom-route"
                    value={formCustomRoute}
                    onChange={(e) => setFormCustomRoute(e.target.value)}
                    className="mt-1.5 w-full px-3 py-2 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:ring-1 focus:ring-black"
                  />
                )}
              </div>

              {/* Optional image update */}
              <div>
                <label className="block font-bold text-gray-700 mb-1">
                  Image URL (Optional)
                </label>
                <input
                  type="url"
                  placeholder="https://example.com/image.png"
                  value={formImageUrl}
                  onChange={(e) => {
                    setFormImageUrl(e.target.value);
                    setImagePreview(e.target.value);
                  }}
                  className="w-full px-3 py-2 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:ring-1 focus:ring-black"
                />
                {imagePreview && (
                  <div className="mt-2 relative inline-block">
                    <img
                      src={imagePreview}
                      alt="Preview"
                      className="h-16 w-28 object-cover rounded-xl border border-gray-200"
                      onError={() => setImagePreview("")}
                    />
                  </div>
                )}
              </div>

              <div className="flex items-center justify-end gap-2 pt-3 border-t border-gray-100">
                <button
                  type="button"
                  onClick={() => setIsEditModalOpen(false)}
                  className="px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-xl font-bold transition"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={actionLoading}
                  className="px-5 py-2 bg-black hover:bg-gray-800 text-white rounded-xl font-bold transition flex items-center gap-1.5 disabled:opacity-50"
                >
                  {actionLoading ? (
                    <>
                      <FiRefreshCw size={13} className="animate-spin" />
                      <span>Saving...</span>
                    </>
                  ) : (
                    <>
                      <FiCheck size={14} />
                      <span>Save Changes</span>
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ======================================================== */}
      {/* 3. VIEW DETAILS MODAL */}
      {/* ======================================================== */}
      {isViewModalOpen && selectedItem && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-xs overflow-y-auto">
          <div className="bg-white rounded-3xl max-w-lg w-full p-6 shadow-2xl border border-gray-100 space-y-4 my-8">
            <div className="flex items-center justify-between border-b border-gray-100 pb-3">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-lg bg-blue-50 text-blue-600 flex items-center justify-center border border-blue-200">
                  <FiEye size={16} />
                </div>
                <div>
                  <h3 className="text-base font-bold text-gray-900">
                    Notification Details #{selectedItem.id}
                  </h3>
                  <p className="text-[11px] text-gray-400">
                    Recorded at {formatUtcDateTime(selectedItem.createdAt)}
                  </p>
                </div>
              </div>
              <button
                type="button"
                onClick={() => setIsViewModalOpen(false)}
                className="text-gray-400 hover:text-black p-1 transition"
              >
                <FiX size={18} />
              </button>
            </div>

            <div className="space-y-3.5 text-xs">
              {/* Type Badge */}
              <div className="flex items-center justify-between">
                <span className="text-gray-500 font-medium">Type:</span>
                <span
                  className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-bold border ${
                    (TYPE_CONFIG[selectedItem.type] || TYPE_CONFIG.event_notification)
                      .badgeClass
                  }`}
                >
                  {(TYPE_CONFIG[selectedItem.type] || TYPE_CONFIG.event_notification).label}
                </span>
              </div>

              {/* Title & Message */}
              <div className="bg-gray-50 rounded-2xl p-3.5 border border-gray-200 space-y-1.5">
                <h4 className="text-sm font-black text-gray-900">
                  {selectedItem.title}
                </h4>
                <p className="text-xs text-gray-700 whitespace-pre-wrap leading-relaxed">
                  {selectedItem.message}
                </p>
              </div>

              {/* Image Preview if present */}
              {selectedItem.imageUrl && (
                <div>
                  <span className="text-gray-500 font-medium block mb-1">Banner Image:</span>
                  <img
                    src={selectedItem.imageUrl}
                    alt="Notification Attachment"
                    className="w-full max-h-48 object-cover rounded-xl border border-gray-200"
                  />
                </div>
              )}

              {/* Event & Target Grid */}
              <div className="grid grid-cols-2 gap-2.5">
                <div className="bg-gray-50 rounded-xl p-3 border border-gray-100">
                  <span className="text-[10px] text-gray-400 font-bold uppercase tracking-wider block">
                    Event
                  </span>
                  <p className="font-bold text-gray-800 mt-0.5 truncate">
                    {selectedItem.eventName || selectedItem.eventId || "Global / All"}
                  </p>
                  {selectedItem.eventId && (
                    <span className="text-[10px] text-gray-400 font-mono">
                      {selectedItem.eventId}
                    </span>
                  )}
                </div>

                <div className="bg-gray-50 rounded-xl p-3 border border-gray-100">
                  <span className="text-[10px] text-gray-400 font-bold uppercase tracking-wider block">
                    Recipient Target
                  </span>
                  <p className="font-bold text-gray-800 mt-0.5 truncate">
                    {selectedItem.userId
                      ? `User #${selectedItem.userId}`
                      : selectedItem.eventId
                      ? "Event Participants"
                      : "All App Users"}
                  </p>
                  {selectedItem.recipientUsername && (
                    <span className="text-[10px] text-gray-500 font-mono">
                      @{selectedItem.recipientUsername}
                    </span>
                  )}
                </div>
              </div>

              {/* Route */}
              <div className="flex items-center justify-between p-2.5 bg-gray-50 rounded-xl border border-gray-100 font-mono text-xs">
                <span className="text-gray-500">Redirect Route:</span>
                <span className="font-bold text-black">
                  {selectedItem.redirectRoute || "/home"}
                </span>
              </div>
            </div>

            <div className="flex items-center justify-end gap-2 pt-3 border-t border-gray-100">
              <button
                type="button"
                onClick={() => {
                  setIsViewModalOpen(false);
                  openDispatchModal(selectedItem);
                }}
                className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl font-bold transition flex items-center gap-1.5"
              >
                <FiSend size={13} /> Dispatch Now
              </button>
              <button
                type="button"
                onClick={() => setIsViewModalOpen(false)}
                className="px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-xl font-bold transition"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ======================================================== */}
      {/* 4. DISPATCH PUSH NOTIFICATION CONFIRMATION MODAL */}
      {/* ======================================================== */}
      {isDispatchModalOpen && selectedItem && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-xs">
          <div className="bg-white rounded-3xl max-w-md w-full p-6 shadow-2xl border border-gray-100 space-y-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-emerald-50 text-emerald-600 border border-emerald-200 flex items-center justify-center flex-shrink-0">
                <FiSend size={18} />
              </div>
              <div>
                <h3 className="text-base font-bold text-gray-900">
                  Dispatch Push Notification
                </h3>
                <p className="text-xs text-gray-400">
                  Send live push notification via Firebase Cloud Messaging
                </p>
              </div>
            </div>

            <div className="bg-gray-50 p-4 rounded-2xl border border-gray-200 space-y-2 text-xs">
              <div>
                <span className="text-gray-400 uppercase text-[10px] font-bold tracking-wider">
                  Target Audience:
                </span>
                <p className="font-bold text-gray-900 mt-0.5">
                  {selectedItem.userId
                    ? `Single User: #${selectedItem.userId} ${
                        selectedItem.recipientUsername
                          ? `(@${selectedItem.recipientUsername})`
                          : ""
                      }`
                    : selectedItem.eventId
                    ? `All Participants of Event: ${
                        selectedItem.eventName || selectedItem.eventId
                      }`
                    : "All Active App Devices (Global Broadcast)"}
                </p>
              </div>

              <div className="pt-2 border-t border-gray-200/60">
                <span className="text-gray-400 uppercase text-[10px] font-bold tracking-wider">
                  Notification Payload:
                </span>
                <p className="font-bold text-gray-800 mt-0.5">{selectedItem.title}</p>
                <p className="text-gray-600 line-clamp-2 mt-0.5">{selectedItem.message}</p>
              </div>
            </div>

            <p className="text-[11px] text-amber-700 bg-amber-50 p-2.5 rounded-xl border border-amber-200">
              ⚠️ This will immediately send a push notification to active mobile device tokens.
            </p>

            <div className="flex items-center justify-end gap-2 pt-2 border-t border-gray-100">
              <button
                type="button"
                onClick={() => setIsDispatchModalOpen(false)}
                disabled={actionLoading}
                className="px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-xl font-bold transition text-xs"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleDispatchSubmit}
                disabled={actionLoading}
                className="px-5 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl font-bold transition text-xs flex items-center gap-1.5 disabled:opacity-50"
              >
                {actionLoading ? (
                  <>
                    <FiRefreshCw size={13} className="animate-spin" />
                    <span>Dispatching...</span>
                  </>
                ) : (
                  <>
                    <FiSend size={13} />
                    <span>Confirm & Send Push</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ======================================================== */}
      {/* 5. TEST FCM PUSH MODAL */}
      {/* ======================================================== */}
      {isTestPushModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-xs overflow-y-auto">
          <div className="bg-white rounded-3xl max-w-lg w-full p-6 shadow-2xl border border-gray-100 space-y-4 my-8">
            <div className="flex items-center justify-between border-b border-gray-100 pb-3">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-lg bg-amber-50 text-amber-600 border border-amber-200 flex items-center justify-center">
                  <FiRadio size={16} />
                </div>
                <div>
                  <h3 className="text-base font-bold text-gray-900">
                    Send Test Push Notification
                  </h3>
                  <p className="text-[11px] text-gray-400">
                    Directly test push notification on developer device using FCM token
                  </p>
                </div>
              </div>
              <button
                type="button"
                onClick={() => setIsTestPushModalOpen(false)}
                className="text-gray-400 hover:text-black p-1 transition"
              >
                <FiX size={18} />
              </button>
            </div>

            <form onSubmit={handleTestPushSubmit} className="space-y-4 text-xs">
              <div>
                <label className="block font-bold text-gray-700 mb-1">
                  Device FCM Token <span className="text-red-500">*</span>
                </label>
                <textarea
                  rows={2}
                  required
                  placeholder="Paste target device FCM Token (e.g. eFM7jWd5SI2jknIK0uxLjJ:APA91b...)"
                  value={testFcmToken}
                  onChange={(e) => setTestFcmToken(e.target.value)}
                  className="w-full px-3 py-2 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:ring-1 focus:ring-black font-mono text-[11px]"
                />
              </div>

              <div>
                <label className="block font-bold text-gray-700 mb-1">
                  Title <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  required
                  value={testTitle}
                  onChange={(e) => setTestTitle(e.target.value)}
                  className="w-full px-3 py-2 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:ring-1 focus:ring-black"
                />
              </div>

              <div>
                <label className="block font-bold text-gray-700 mb-1">
                  Message <span className="text-red-500">*</span>
                </label>
                <textarea
                  rows={2}
                  required
                  value={testMessage}
                  onChange={(e) => setTestMessage(e.target.value)}
                  className="w-full px-3 py-2 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:ring-1 focus:ring-black resize-none"
                />
              </div>

              <div className="grid grid-cols-2 gap-2.5">
                <div>
                  <label className="block font-bold text-gray-700 mb-1">
                    Redirect Route
                  </label>
                  <select
                    value={testRoute}
                    onChange={(e) => setTestRoute(e.target.value)}
                    className="w-full px-3 py-2 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:ring-1 focus:ring-black font-medium"
                  >
                    {NOTIFICATION_REDIRECT_ROUTES.map((route) => (
                      <option key={route.value} value={route.value}>
                        {route.label} ({route.value})
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block font-bold text-gray-700 mb-1">
                    Associated Event (Optional)
                  </label>
                  <select
                    value={testEventId}
                    onChange={(e) => setTestEventId(e.target.value)}
                    className="w-full px-3 py-2 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:ring-1 focus:ring-black font-medium"
                  >
                    <option value="">None</option>
                    {eventsList.map((evt) => (
                      <option key={evt.eventId || evt.id} value={evt.eventId || evt.id}>
                        {evt.eventName}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="flex items-center justify-end gap-2 pt-3 border-t border-gray-100">
                <button
                  type="button"
                  onClick={() => setIsTestPushModalOpen(false)}
                  className="px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-xl font-bold transition"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={actionLoading}
                  className="px-5 py-2 bg-amber-600 hover:bg-amber-700 text-white rounded-xl font-bold transition flex items-center gap-1.5 disabled:opacity-50"
                >
                  {actionLoading ? (
                    <>
                      <FiRefreshCw size={13} className="animate-spin" />
                      <span>Sending Test...</span>
                    </>
                  ) : (
                    <>
                      <FiSend size={14} />
                      <span>Send Test Push</span>
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ======================================================== */}
      {/* 6. DELETE NOTIFICATION CONFIRMATION MODAL */}
      {/* ======================================================== */}
      {isDeleteModalOpen && selectedItem && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-xs">
          <div className="bg-white rounded-3xl max-w-sm w-full p-6 shadow-2xl border border-gray-100 space-y-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-red-50 text-red-600 border border-red-200 flex items-center justify-center flex-shrink-0">
                <FiTrash2 size={18} />
              </div>
              <div>
                <h3 className="text-base font-bold text-gray-900">
                  Delete Notification
                </h3>
                <p className="text-xs text-gray-400">
                  This action cannot be undone
                </p>
              </div>
            </div>

            <p className="text-xs text-gray-600">
              Are you sure you want to delete notification{" "}
              <strong className="text-gray-900 font-bold">
                "{selectedItem.title}"
              </strong>{" "}
              (ID #{selectedItem.id})?
            </p>

            <div className="flex items-center justify-end gap-2 pt-2 border-t border-gray-100 text-xs">
              <button
                type="button"
                onClick={() => setIsDeleteModalOpen(false)}
                disabled={actionLoading}
                className="px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-xl font-bold transition"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleDeleteSubmit}
                disabled={actionLoading}
                className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-xl font-bold transition flex items-center gap-1.5 disabled:opacity-50"
              >
                {actionLoading ? (
                  <>
                    <FiRefreshCw size={12} className="animate-spin" />
                    <span>Deleting...</span>
                  </>
                ) : (
                  <>
                    <FiTrash2 size={13} />
                    <span>Delete</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EventNotifications;
